// useNodePeekData.js

import { useEffect, useState } from 'react';

import { subscribeToNode } from 'services/nodes/NodeReadService';

export default function useNodePeekData({ isOpen, peekNodeKey, peekSeedNode }) {
  // ----- STATE ----- //

  // Loaded Peek Node
  const [peekNode, setPeekNode] = useState(null);

  // Peek Loading State
  const [isLoading, setIsLoading] = useState(false);

  // Peek Error State
  const [error, setError] = useState('');

  // ----- EFFECTS ----- //

  // Subscribes to the peeked node so backend automation writes are reflected live.
  useEffect(() => {
    if (!isOpen) {
      setPeekNode(null);
      setIsLoading(false);
      setError('');
      return undefined;
    }

    const key = typeof peekNodeKey === 'string' ? peekNodeKey.trim() : '';
    if (!key) {
      setPeekNode(null);
      setIsLoading(false);
      setError('');
      return undefined;
    }

    const seedMatches = peekSeedNode?.key === key;
    if (seedMatches) {
      // Seed is enough to render immediately; live snapshot can replace later.
      setPeekNode((prev) => (prev?.key === key ? prev : peekSeedNode));
      setIsLoading(false);
    } else {
      setPeekNode(null);
      setIsLoading(true);
    }

    setError('');

    let didReceiveInitialSnapshot = false;

    const unsubscribe = subscribeToNode(key, (loaded) => {
      if (!didReceiveInitialSnapshot) {
        didReceiveInitialSnapshot = true;
        setIsLoading(false);
      }

      if (loaded) {
        setPeekNode(loaded);
        setError('');
        return;
      }

      // Keep a matching seed visible while the first snapshot is still empty
      // (e.g. brief replication lag right after create).
      if (seedMatches) {
        setPeekNode((prev) => prev || peekSeedNode);
        setError('');
        return;
      }

      setPeekNode(null);
      setError('This item has been deleted.');
    });

    return () => {
      unsubscribe?.();
    };
  }, [
    isOpen,
    peekNodeKey,
    peekSeedNode,
  ]);

  return {
    peekNode,
    isLoading,
    error,
  };
}
